<template>
  <div>
    <v-btn style="z-index: 1000" absolute top right icon color="red" @click="$emit('cancel-edit')">
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <v-row no-gutters class="justify-center mb-4">
      <v-col v-for="(exercise, exIndex) in localExercises" :key="exIndex" cols="auto" class="mx-2 d-flex flex-column align-center">
        <v-avatar size="80" class="elevation-4">
          <v-img cover :src="getImageForExercise(exercise)" position="top center"></v-img>
        </v-avatar>
        <v-card-title class="pa-1 text-center" style="font-size: 1rem; line-height: 1.2">
          {{ exercise.title }}
        </v-card-title>
      </v-col>
    </v-row>

    <v-divider class="mb-4"></v-divider>

    <v-card-title>Sätze</v-card-title>
    <v-card-text>
      <div v-if="commonSets.length > 0" class="mb-4">
        <div
          v-for="(set, setIndex) in commonSets"
          :key="setIndex"
          class="d-flex align-center mb-2 pa-2 rounded-lg"
          style="background: rgba(255, 255, 255, 0.05)">
          <v-avatar size="32" class="mr-3">
            <v-img :src="getImageForExercise(localExercises[set.exerciseIndex])"></v-img>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="text-caption grey--text">{{ localExercises[set.exerciseIndex].title }}</span>
            <span class="font-weight-bold">{{ formatSetValues(set.values) }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon small color="red" @click="removeSet(setIndex)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-else class="text-center grey--text my-4">Noch keine Sätze hinzugefügt.</div>

      <v-expand-transition>
        <div v-if="isAddingSet">
          <v-card outlined class="pa-4 mt-4" color="#3e3e3e">
            <v-card-title class="pa-0 mb-3">
              Satz für <span class="primary--text mx-1">{{ localExercises[addingSetForExerciseIndex].title }}</span> hinzufügen
            </v-card-title>
            <v-row no-gutters>
              <v-col v-for="(setting, index) in orderedNewSetData" :key="index" cols="auto" class="pa-1">
                <v-card class="input-card" flat color="rgba(255,255,255,0.1)">
                  <div class="text-caption grey--text text--lighten-1 px-2 pt-1">{{ shortenLabel(setting.text) }}</div>
                  <v-text-field
                    v-model="setting.value"
                    :type="setting.input === 'number' ? 'number' : 'text'"
                    :suffix="setting.text === 'Pause' ? 'sek' : setting.text === 'Gewicht' ? 'kg' : ''"
                    dense
                    solo
                    flat
                    hide-details
                    class="centered-input"
                    @keydown.enter.prevent="saveNewSet()"></v-text-field>
                </v-card>
              </v-col>
            </v-row>
            <v-card-actions class="pa-0 mt-3">
              <v-spacer></v-spacer>
              <v-btn text @click="cancelAddSet">Abbrechen</v-btn>
              <v-btn color="primary" @click="saveNewSet">Satz speichern</v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-expand-transition>

      <div v-if="!isAddingSet" class="d-flex justify-space-around mt-6">
        <v-btn v-for="(exercise, exIndex) in localExercises" :key="exIndex" color="primary" outlined @click="addSetForExercise(exIndex)">
          <v-icon left>mdi-plus</v-icon>
          Satz für {{ exercise.title.split(" ")[0] }}
        </v-btn>
      </div>
    </v-card-text>

    <v-divider class="my-4"></v-divider>

    <v-card-actions class="justify-center mt-6">
      <v-btn class="rounded-xl" x-large color="primary" @click="saveSuperset"> Supersatz speichern </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
export default {
  name: "WorkoutSplitEditExerciseMulti",
  props: {
    exercisesProp: {
      type: Array,
      required: true,
    },
    supersetId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      localExercises: [],
      commonSets: [],
      isAddingSet: false,
      addingSetForExerciseIndex: null,
      newSetData: [],
    }
  },
  created() {
    this.localExercises = JSON.parse(JSON.stringify(this.exercisesProp))
    this.commonSets = []

    const maxSets = Math.max(...this.localExercises.map((ex) => (ex.sets ? ex.sets.length : 0)))

    for (let i = 0; i < maxSets; i++) {
      this.localExercises.forEach((exercise, exIndex) => {
        if (exercise.sets && exercise.sets[i]) {
          this.commonSets.push({
            exerciseIndex: exIndex,
            values: exercise.sets[i],
          })
        }
      })
    }
  },
  computed: {
    orderedNewSetData() {
      if (!this.newSetData) return []
      const order = ["Gewicht", "Dauer", "Min Wiederholungen", "Max Wiederholungen", "Pause", "Notiz"]
      const settings = [...this.newSetData]

      return settings.sort((a, b) => {
        const aIndex = order.indexOf(a.text)
        const bIndex = order.indexOf(b.text)
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      })
    },
  },
  methods: {
    shortenLabel(text) {
      return text.replace("Wiederholungen", "WH")
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return this.getImageUrl(exercise.picture)
      if (exercise.manual && exercise.manual[0]?.picture) return this.getImageUrl(exercise.manual[0].picture)
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    getImageUrl(image) {
      if (!image) return ""
      return `/img/uebungen/${image.replace(/\.[^/.]+$/, "")}.webp`
    },

    // NEU: Formattiert die Satz-Daten für eine schöne Anzeige
    formatSetValues(values) {
      const getVal = (text) => values.find((v) => v.text.includes(text))?.value

      const weight = getVal("Gewicht")
      const minReps = getVal("Min Wiederholungen")
      const maxReps = getVal("Max Wiederholungen") || minReps
      const reps = getVal("Wiederholungen")
      const duration = getVal("Dauer")

      let parts = []
      if (weight) parts.push(`${weight}kg`)

      let repString = ""
      if (minReps && maxReps) {
        repString = minReps === maxReps ? `${minReps} Wdh.` : `${minReps}-${maxReps} Wdh.`
      } else if (reps) {
        repString = `${reps} Wdh.`
      }
      if (repString) parts.push(repString)

      if (duration) parts.push(`${duration} sek`)

      return parts.join(" / ") || "Keine Werte"
    },

    // ÜBERARBEITET: Füllt das Formular mit den Daten des letzten Satzes vor
    addSetForExercise(exerciseIndex) {
      this.addingSetForExerciseIndex = exerciseIndex

      const settingsTemplate = this.localExercises[exerciseIndex].set_exercise_settings || [
        { text: "Gewicht", value: "", input: "number" },
        { text: "Wiederholungen", value: "", input: "number" },
      ]
      this.newSetData = JSON.parse(JSON.stringify(settingsTemplate))

      // Finde den letzten Satz für diese Übung
      const lastSetForThisExercise = this.commonSets
        .slice()
        .reverse()
        .find((set) => set.exerciseIndex === exerciseIndex)

      if (lastSetForThisExercise) {
        // Fülle das Formular mit den Werten des letzten Satzes
        this.newSetData.forEach((setting) => {
          const lastValue = lastSetForThisExercise.values.find((v) => v.text === setting.text)
          setting.value = lastValue ? lastValue.value : ""
        })
      } else {
        // Kein vorheriger Satz, also leere Felder
        this.newSetData.forEach((s) => (s.value = ""))
      }

      this.isAddingSet = true
    },
    cancelAddSet() {
      this.isAddingSet = false
      this.addingSetForExerciseIndex = null
      this.newSetData = []
    },
    saveNewSet() {
      const finalSetValues = this.newSetData.filter((s) => s.value)

      if (finalSetValues.length > 0) {
        this.commonSets.push({
          exerciseIndex: this.addingSetForExerciseIndex,
          values: finalSetValues,
        })
      }
      this.cancelAddSet()
    },
    removeSet(setIndex) {
      this.commonSets.splice(setIndex, 1)
    },
    saveSuperset() {
      this.localExercises.forEach((ex) => (ex.sets = []))
      this.commonSets.forEach((set) => {
        if (this.localExercises[set.exerciseIndex]) {
          this.localExercises[set.exerciseIndex].sets.push(set.values)
        }
      })

      if (this.localExercises.length > 0) {
        const firstSetCount = this.localExercises[0].sets.length
        if (firstSetCount === 0) {
          alert("Bitte füge mindestens einen Satz für jede Übung hinzu.")
          return
        }
        const allHaveSameSetCount = this.localExercises.every((ex) => ex.sets.length === firstSetCount)
        if (!allHaveSameSetCount) {
          alert("Jede Übung im Supersatz muss die gleiche Anzahl an Sätzen haben.")
          return
        }
      }

      const finalSuperset = {
        isSuperset: true,
        supersetId: this.supersetId || `superset-${Date.now()}`,
        exercises: this.localExercises,
      }

      this.$emit("savedExercise", finalSuperset)
    },
  },
}
</script>

<style lang="scss" scoped>
.input-card {
  border-radius: 12px;
  transition: box-shadow 0.2s ease-out;
  width: 110px;
  &:focus-within {
    box-shadow: 0 0 10px rgba(var(--v-primary-base-rgb), 0.5);
  }
}

::v-deep .centered-input input {
  text-align: center;
}
</style>
